import { useCallback, useEffect, useState } from "react";

/** Per-folder open/closed state, persisted per repo. A path present in the map
 * is an explicit user (or collapse-all/expand-all) choice; absent falls back to
 * the tree's default heuristic. */
type State = Record<string, boolean>;

const key = (repo: string): string => `dw-tree-collapse:${repo}`;

function load(repo: string): State {
  try {
    const raw = localStorage.getItem(key(repo));
    return raw ? (JSON.parse(raw) as State) : {};
  } catch {
    return {};
  }
}

function store(repo: string, state: State): void {
  try {
    localStorage.setItem(key(repo), JSON.stringify(state));
  } catch {
    /* private mode / quota — collapse state just won't persist */
  }
}

export interface Collapse {
  /** Resolved open state for a folder; `fallback` is the tree's default. */
  isOpen: (path: string, fallback: boolean) => boolean;
  /** Record a single folder's open state (a manual toggle). */
  setOpen: (path: string, open: boolean) => void;
  /** Set many folders at once — powers collapse-all / expand-all. */
  setAll: (paths: string[], open: boolean) => void;
}

export function useCollapse(repo: string): Collapse {
  const [state, setState] = useState<State>(() => load(repo));

  // Reload when switching repos; persist on every change.
  useEffect(() => setState(load(repo)), [repo]);
  useEffect(() => store(repo, state), [repo, state]);

  const isOpen = useCallback(
    (path: string, fallback: boolean): boolean =>
      path in state ? state[path] : fallback,
    [state],
  );
  const setOpen = useCallback(
    (path: string, open: boolean): void =>
      setState((s) => ({ ...s, [path]: open })),
    [],
  );
  const setAll = useCallback(
    (paths: string[], open: boolean): void =>
      setState((s) => {
        const next = { ...s };
        for (const p of paths) next[p] = open;
        return next;
      }),
    [],
  );

  return { isOpen, setOpen, setAll };
}

/** Every directory path implied by a flat file list (all prefixes). */
export function folderPaths(files: string[]): string[] {
  const dirs = new Set<string>();
  for (const file of files) {
    const parts = file.split("/");
    for (let i = 1; i < parts.length; i++) {
      dirs.add(parts.slice(0, i).join("/"));
    }
  }
  return [...dirs];
}

// Folders that start collapsed unless overridden — too noisy to show open.
const COLLAPSED_BY_DEFAULT = ["queries", "images"];

/** The tree's default open state for a folder, before any stored override. */
export function defaultOpen(name: string): boolean {
  return !COLLAPSED_BY_DEFAULT.includes(name.toLowerCase());
}

/** Is any collapsible folder currently expanded? Folders force-open because
 * they hold the current page don't count — you can't collapse those, so they
 * shouldn't keep the toggle stuck on "collapse". Drives the single fold button:
 * any open → offer collapse-all; none → offer expand-all. */
export function anyOpen(dirs: string[], currentPath: string, collapse: Collapse): boolean {
  return dirs.some((path) => {
    const within = currentPath === path || currentPath.startsWith(`${path}/`);
    if (within) return false;
    const name = path.slice(path.lastIndexOf("/") + 1);
    return collapse.isOpen(path, defaultOpen(name));
  });
}
